import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../utils/auth";
import HealthLensScreen2 from "../assets/images/HealthLensScreen2.jpg";
import { ReactComponent as Arrow } from "../assets/icons/Arrow.svg";
import { ReactComponent as Info } from "../assets/icons/Info.svg";

const AgentCard = ({ agentInfo }) => {
  const [showCode, setShowCode] = useState(false);
  if (!agentInfo) return null;
  const initials = agentInfo.agent_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const maskedCode = agentInfo.code ? agentInfo.code.replace(/./g, "\u2022") : "";

  return (
    <div className="flex items-center space-x-3 p-4 rounded-2xl bg-gradient-to-r from-[#1845a3]/5 via-white to-[#ED1C24]/5 border border-[#1845a3]/10">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center shadow-md">
        <span className="text-white text-sm font-semibold">{initials}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-lightgray font-medium uppercase tracking-wide">Your Agent</p>
        <p className="text-sm text-primary font-semibold truncate">{agentInfo.agent_name}</p>
        <div className="flex items-center space-x-1.5 mt-0.5">
          <svg className="w-3 h-3 text-[#ED1C24] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
          <span className="text-xxs text-darkgray font-medium">{showCode ? agentInfo.code : maskedCode}</span>
          <button type="button" onClick={() => setShowCode(!showCode)} className="flex-shrink-0 p-0.5">
            {showCode ? (
              <svg className="w-3.5 h-3.5 text-darkgray" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.5 6.5m7.378 7.378L17.5 17.5M3 3l18 18" /></svg>
            ) : (
              <svg className="w-3.5 h-3.5 text-darkgray" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            )}
          </button>
        </div>
      </div>
      <div className="flex-shrink-0 px-2.5 py-1 rounded-full bg-green-100 border border-green-200">
        <span className="text-xxs text-green-700 font-medium">Verified</span>
      </div>
    </div>
  );
};

const TechCard = ({ icon, title, description }) => (
  <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-box">
    <div className="flex items-start space-x-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-semibold text-primary">{title}</h4>
        <p className="mt-1 text-xxs text-darkgray leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { agentInfo } = useAuth();

  return (
    <section className="p-6 pt-0 pb-8 space-y-5">
      <AgentCard agentInfo={agentInfo} />

      <Link to="/basic-details" className="block w-full rounded-2xl overflow-hidden shadow-lg relative">
        <img className="w-full object-cover" src={HealthLensScreen2} alt="Digital Health Assessment" />
        <div className="absolute bottom-6 left-6 flex items-center space-x-2 px-4 py-2 rounded-full bg-[#1845a3] text-white shadow-md">
          <span className="text-xs font-semibold">Start A Scan</span>
          <Arrow className="shrink-0 h-3 w-3 text-white" />
        </div>
      </Link>


      <div>
        <div className="flex items-center space-x-2 mb-3">
          <Info className="flex-shrink-0 w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-primary">How It Works</h3>
        </div>
        <div className="space-y-3">
          <TechCard
            icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>}
            title="PPG (Photoplethysmography)"
            description="PPG is an optical technique that detects blood volume changes in tissue by shining light and measuring absorption. Traditionally used in pulse oximeters and smartwatches, it captures your heart rate, oxygen levels, and blood flow patterns through direct skin contact."
          />
          <TechCard
            icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>}
            title="rPPG (Remote Photoplethysmography)"
            description="rPPG takes PPG technology further by eliminating physical contact entirely. Using your smartphone's front camera, it analyzes subtle color variations on your face caused by blood flow beneath the skin -- invisible to the naked eye but detectable by advanced algorithms."
          />
          <TechCard
            icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
            title="What We Measure"
            description="In just 60 seconds, our scan captures heart rate, blood pressure, oxygen saturation, stress levels, HRV, BMI, respiration rate, and cardiac output. All vitals are processed using clinical-grade algorithms to give you a comprehensive health overview."
          />
          <TechCard
            icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
            title="Safe & Private"
            description="Your scan data is processed securely and never shared without consent. The camera feed is analyzed in real-time and is not recorded or stored. Your health data remains private and protected."
          />
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
